import { AgeDividedReportEntry, CUSTOMER, NewSubAccount, SubAccount, SubAccountInvoiceBalance, SubAccountRelationType } from '@/subaccount/types/SubAccount'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { get, httpDelete, post, put } from '@/services/infrastructure/api.service'
import { TransactionSearchParams } from '@/types/transaction/TransactionSearchParams'
import { useSingletonEmitter } from '@/services/infrastructure/emitter'
import { LOGOUT, NO_ORG_SELECTED, ORG_SELECTED, ORG_SWITCHED } from '@/types/events'
import { usePanelManager } from '@tritt/multipanel'
import { searchTransactionLines } from '@/transaction/transaction.service'
import { SubAccountLine } from '@/subaccount/outstandingBalance/SubAccountLine'
import { SubAccountLedgerFilter } from '@/subaccount/outstandingBalance/SubAccountLedgerFilter'
import { LedgerSubAccount, SubAccountLedger } from '@/subaccount/types/SubAccountLedger'
import { SYS_MIN_DATE, toIsoDate } from '@/dateUtils/dateUtils'
import { SaccLineConnUuid } from '@/subaccount/outstandingBalance/SaccLineConn'
import { emptyPage, Page } from '@/types/Page'
import { getBrgSearch } from '@tritt/brg-search-client-ts'
import { Organization } from '@tritt/brg-search-client-ts/lib/types'
import { ensureValidNewSubAccount } from '@/subaccount/subAccount.validators'
import { getSelectedOrgId, orgSelectionComplete } from '@/tenants/org.service'
import { KraviaReminder, KraviaReminderEnum, SubAccountDebtCollectionSettings } from '@/debt-collection/KraviaReminder'
import NewSubAccountPopup from '@/subaccount/NewSubAccountPopup.vue'
import useDynamicComponentLoader from '@/dynamic-component-loader/dynamicComponent.service'
import { useContaCommand } from '@/commandbar/commandBar.service'
import { SUBACC_DETAILS } from '@/subaccount/index'
import { useSetting } from '@/setting/setting.service'
import { cloneDeep, isEqual } from 'lodash'
import { useSubAccountValidation } from '@/subaccount/subAccountValidator'
import { defaultAddress, defaultContactInfo } from '@/subaccount/SubAccount.builder'
import { notifyError } from '@/services/infrastructure/notification.service'
import { InvoiceData } from '@/invoice/invoice.service'
import { Fulfillment } from '@/assignments/Assignment.type'

const CUSTOMERS_PATH = '/customers'
const SUPPLIERS_PATH = '/suppliers'
const SUB_ACCOUNT_PATH = '/sub-accounts'
const DEBT_COLLECTION_SETTINGS_PATH = '/debt-collection/customer-settings'
const LEDGER = '/ledger'
const SUB_ACCOUNT_BALANCE = '/balance'
const CUSTOMER_CONTA_COMMAND_TYPE = 'customer'
const SUPPLIER_CONTA_COMMAND_TYPE = 'supplier'

const BrgSearch = getBrgSearch()
const { loadComponent } = useDynamicComponentLoader()
const { registerCommand, unregisterCommandsOfType } = useContaCommand()


export const customers = ref<SubAccount[]>([])
export const suppliers = ref<SubAccount[]>([])

//TODO: Rename. Maybe fetchCustomers + refreshCustomerStore? Same for suppliers
const getCustomers = async (tenantId?: string): Promise<SubAccount[]> => {
    return await get<SubAccount[]>(SUB_ACCOUNT_PATH + CUSTOMERS_PATH, {}, tenantId) ?? []
}

const registerSubAccDetailsCommand = (subAcc: SubAccount) => {
    const isCustomer = subAcc.relationType === CUSTOMER
    registerCommand({
        type: isCustomer ? CUSTOMER_CONTA_COMMAND_TYPE : SUPPLIER_CONTA_COMMAND_TYPE,
        typeLabel: isCustomer ? 'Kunde' : 'Leverandør',
        label: subAcc.name,
        key: subAcc.uuid,
        showPanelSelector: true,
        icon: isCustomer ? 'i-ph-user-fill' : 'i-ph-truck-fill',
        activate: (targetPanelId: number) => {
            const { setPanelPath } = usePanelManager()
            setPanelPath({
                ...SUBACC_DETAILS,
                panelId: targetPanelId,
                queryValues: { subAccountUuid: subAcc.uuid }
            })
        },
        isActive: () => false
    })
}

const fetchCustomers = async () => {
    if (!getSelectedOrgId()) {
        return
    }
    try {
        // TODO cleanup typing
        const [customerData, invoiceData, debtCollectionSettingsData] = (await Promise.all([
            getCustomers(),
            get<SubAccountInvoiceBalance[]>(SUB_ACCOUNT_PATH + CUSTOMERS_PATH + SUB_ACCOUNT_BALANCE, {}),
            get<SubAccountDebtCollectionSettings[]>(DEBT_COLLECTION_SETTINGS_PATH + '/all', {})
        ])).map(result => result ?? []) as [SubAccount[], SubAccountInvoiceBalance[], SubAccountDebtCollectionSettings[]]
        if (!customerData) {
            customers.value = []
            return
        }
        customers.value = customerData
        customers.value.forEach(registerSubAccDetailsCommand)
        const debtCollectionSettingsMap = new Map<string, SubAccountDebtCollectionSettings>()
        debtCollectionSettingsData.forEach((settings: SubAccountDebtCollectionSettings) => {
            debtCollectionSettingsMap.set(settings.subAccountUuid, settings)
        })

        const customersWithSettings = customerData.map((customer: SubAccount) => {
            const settings = debtCollectionSettingsMap.get(customer.uuid)
            return {
                ...customer,
                customerDebtCollectionSettings: settings || null
            }
        })

        customers.value = customersWithSettings
        addInvoiceBalance(customers.value, invoiceData)
    } catch (e: any) {
        notifyError({ title: 'Feil ved henting av kunder', description: { descriptionMessage: 'Det skjedde en uventet feil ved henting av kundedata. Feilen er logget.', error: e } })
    }
}

const getSuppliers = async (tenantId?: string): Promise<SubAccount[]> => {
    return await get(SUB_ACCOUNT_PATH + SUPPLIERS_PATH, {}, tenantId) ?? []
}

const fetchSuppliers = async () => {
    if (!getSelectedOrgId()) {
        return
    }
    try {
        // TODO cleanup typing
        const [supplierData, invoiceData] = (await Promise.all([
            getSuppliers(),
            get<InvoiceData[]>(SUB_ACCOUNT_PATH + SUPPLIERS_PATH + SUB_ACCOUNT_BALANCE, {})
        ])).map(result => result ?? []) as [SubAccount[], SubAccountInvoiceBalance[]]
        if (!supplierData) {
            suppliers.value = []
            return
        }
        suppliers.value = supplierData
        suppliers.value.forEach(registerSubAccDetailsCommand)
        addInvoiceBalance(suppliers.value, invoiceData)
    } catch (e: any) {
        notifyError({ title: 'Feil ved henting av leverandører', description: { descriptionMessage: 'Det skjedde en uventet feil ved henting av leverandørdata. Feilen er logget.', error: e } })
    }
}


const createSubAccount = async (sacc: NewSubAccount, fulfillment?: Fulfillment) => {
    try {
        const newSacc = await post(SUB_ACCOUNT_PATH, {
            fulfillment,
            ...ensureValidNewSubAccount(sacc)
        })
        if (newSacc.relationType === CUSTOMER) {
            await post(DEBT_COLLECTION_SETTINGS_PATH, {
                subAccountUuid: newSacc.uuid,
                customerSettingsEnabled: sacc.customerDebtCollectionSettings?.customerSettingsEnabled || false,
                reminderType: sacc.customerDebtCollectionSettings?.reminderType || kraviaFirstReminderTypeSetting.value?.value as KraviaReminder,
                intervalDays: sacc.customerDebtCollectionSettings?.intervalDays || 14,
            })
        }
        return newSacc
    } catch (e: unknown) {
        const type = sacc.relationType === 'CUSTOMER' ? 'Kunden' : 'Leverandøren'
        notifyError({ title: 'Feilet', description: { descriptionMessage: `${type} ${sacc.name} kunne ikke opprettes.`, error: e } })
        throw e
    }

}

// @ts-ignore
if (window.Cypress) {
    // @ts-ignore
    window.createSubAccount = createSubAccount
}


const deleteSubAccount = async (uuid: string) => {
    try {
        await post(`${DEBT_COLLECTION_SETTINGS_PATH}/:delete`, { subAccountUuid: uuid })
        return await httpDelete(`${SUB_ACCOUNT_PATH}/${uuid}`)
    } catch (e: unknown) {
        const sacc = findSubAccountByUuid(uuid)
        let descriptionMessage = 'Kunne ikke å slette kunde/leverandør.'

        if (sacc) {
            const type = sacc.relationType === 'CUSTOMER' ? 'Kunden' : 'Leverandøren'
            descriptionMessage = `${type} ${sacc.name} kunne ikke slettes.`
        }

        notifyError({
            title: 'Feilet',
            description: { descriptionMessage, error: e }
        })

        throw e
    }
}

const updateSubAccount = async (sacc: SubAccount) => {

    try {
        const subAccount = await put(SUB_ACCOUNT_PATH, {
            ...ensureValidNewSubAccount(sacc)
        })
        if (subAccount.relationType === CUSTOMER) {
            await put(DEBT_COLLECTION_SETTINGS_PATH, {
                subAccountUuid: sacc.uuid,
                customerSettingsEnabled: sacc.customerDebtCollectionSettings?.customerSettingsEnabled || false,
                reminderType: sacc.customerDebtCollectionSettings?.reminderType || KraviaReminderEnum.enum.DEBT_COLLECTION_NOTICE,
                intervalDays: sacc.customerDebtCollectionSettings?.intervalDays || 14
            })
        }
        return subAccount
    } catch (e: unknown) {
        const type = sacc.relationType === 'CUSTOMER' ? 'Kunden' : 'Leverandøren'
        const descriptionMessage = `${type} ${sacc.name} kunne ikke oppdateres.`

        notifyError({
            title: 'Feilet',
            description: { descriptionMessage, error: e }
        })
        throw e
    }
}

const findSubAccountByUuid = (uuid: string): SubAccount => {
    return [...customers.value, ...suppliers.value].find(subAcc => subAcc.uuid === uuid) as SubAccount
}

const findSubAccountByName = (name: string, ignoreCase?: boolean): SubAccount => {
    return [...customers.value, ...suppliers.value].find((subAcc) => {
        if (ignoreCase) {
            return subAcc.name.toUpperCase() === name.trim().toUpperCase()
        }
        return subAcc.name === name.trim()
    }) as SubAccount
}

const findSubAccountByOrgNo = (orgNo: string): SubAccount => {
    return [...customers.value, ...suppliers.value]
        .find(subAcc => subAcc.orgNo === orgNo.trim()) as SubAccount
}

const findSupplierByUuid = (uuid: string) => {
    return suppliers.value.find(subAcc => subAcc.uuid === uuid) as SubAccount
}

const findSupplierByName = (name: string, ignoreCase?: Boolean) => {
    return suppliers.value.find((subAcc) => {
        if (ignoreCase) {
            return subAcc.name.toUpperCase() === name.trim().toUpperCase()
        }
        return subAcc.name === name.trim()
    }) as SubAccount
}

const findSupplierByOrgNo = (orgNo: string): SubAccount | undefined => {
    return suppliers.value.find(subAcc => subAcc.orgNo === orgNo)
}

const findCustomerByName = (name: string, ignoreCase?: Boolean) => {
    return customers.value.find((subAcc) => {
        if (ignoreCase) {
            return subAcc.name.toUpperCase() === name.trim().toUpperCase()
        }
        return subAcc.name === name.trim()
    }) as SubAccount
}

const findCustomerByOrgNo = (orgNo: string): SubAccount => {
    return customers.value
        .find(subAcc => subAcc.orgNo === orgNo.trim()) as SubAccount
}

// TODO: This is not validating since it is adding fields
const validateAddressAndContactInfo = (subAccount: SubAccount) => {
    if (!subAccount.address) {
        subAccount.address = defaultAddress()
    }
    if (!subAccount.contactInfo) {
        subAccount.contactInfo = defaultContactInfo()
    }
    return subAccount
}

const validateDebtCollectionInfo = (subAccount: SubAccount) => {
    if (!subAccount.customerDebtCollectionSettings) {
        subAccount.customerDebtCollectionSettings = {
            subAccountUuid: subAccount.uuid,
            customerSettingsEnabled: false,
            reminderType: KraviaReminderEnum.enum.DEBT_COLLECTION_NOTICE,
            intervalDays: 14
        }
    }
}

const fetchSubAccounts = async () => {
    return Promise.all([
        fetchCustomers(),
        fetchSuppliers()
    ])
}

const { setting: kraviaFirstReminderTypeSetting } = useSetting<string>('KRAVIA_FIRST_REMINDER_TYPE')

export const createSubAccountFromOrgNo = async (orgNo: string, type: SubAccountRelationType): Promise<SubAccount> => {
    const foundOrg: Organization = await BrgSearch.getOrganization(orgNo.replace(/\D/g, ''))
    if (!foundOrg) {
        throw new Error(`Fant ingen organisasjon med orgnr ${orgNo}`)
    }
    const subAccount: NewSubAccount = {
        name: foundOrg.name,
        orgNo: foundOrg.number,
        relationType: type,
        isBusiness: true,
        contactInfo: {
            name: '',
            email: '',
            phoneNumber: ''
        },
        metadata: { suggestions: {} },
        notes: '',
        active: true,
        address: {
            addressLine: foundOrg.billingAddress.streetAddress.join(', '),
            postalCode: foundOrg.billingAddress.postalCode,
            postalLocation: foundOrg.billingAddress.postalPlace,
            countryCode: foundOrg.billingAddress.countryCode
        },
        customerDebtCollectionSettings: {
            subAccountUuid: '',
            customerSettingsEnabled: false,
            reminderType: kraviaFirstReminderTypeSetting.value?.value as KraviaReminder,
            intervalDays: 14
        }
    }

    const createdAccount = await createSubAccount(subAccount)
    if (type === CUSTOMER) {
        customers.value.push(createdAccount)
    } else {
        suppliers.value.push(createdAccount)
    }
    return createdAccount
}

export const createSubAccountFromOnlyName = async (name: string, type: SubAccountRelationType) => {
    const subAccount: NewSubAccount = {
        name: name,
        orgNo: '',
        relationType: type,
        isBusiness: false,
        contactInfo: {
            name: name,
            email: '',
            phoneNumber: ''
        },
        metadata: { suggestions: {} },
        notes: '',
        active: true,
        address: {
            addressLine: '',
            postalCode: '',
            postalLocation: '',
            countryCode: ''
        },
        customerDebtCollectionSettings: {
            subAccountUuid: '',
            customerSettingsEnabled: false,
            reminderType: kraviaFirstReminderTypeSetting.value?.value as KraviaReminder,
            intervalDays: 14
        }
    }

    const createdAccount = await createSubAccount(subAccount)
    if (type === CUSTOMER) {
        customers.value.push(createdAccount)
    } else {
        suppliers.value.push(createdAccount)
    }
    return createdAccount
}

const addInvoiceBalance = (subAccounts: SubAccount[], subAccountInvoice: Array<SubAccountInvoiceBalance>) => {
    subAccountInvoice.forEach((invoice) => {
        const subAccount = subAccounts.find((account) => account.uuid === invoice.subAccountUuid)
        if (subAccount) {
            subAccount.totalUnpaid = invoice.totalUnpaid
            subAccount.totalInvoiced = invoice.totalInvoiced
        }
    })
}
const findTransactionLinesBySubAccountUuid = async (searchParams: TransactionSearchParams) => {
    return await searchTransactionLines(searchParams)
}

const getAccountsPayable = async (filter: SubAccountLedgerFilter): Promise<SubAccountLedger | undefined> => {
    if (filter.isOpen || !filter.start) {
        filter.start = SYS_MIN_DATE
    }

    try {
        return await get(SUB_ACCOUNT_PATH + SUPPLIERS_PATH + LEDGER, {
            fromDate: toIsoDate(filter.start),
            toDate: toIsoDate(filter.end),
            isOpen: filter.isOpen,
            name: filter.textQuery
        })
    } catch (e: any) {
        console.error(e)
    }
}

const getAccountsReceivable = async (filter: SubAccountLedgerFilter): Promise<SubAccountLedger | undefined> => {
    if (filter.isOpen || !filter.start) {
        filter.start = SYS_MIN_DATE
    }

    try {
        return await get(SUB_ACCOUNT_PATH + CUSTOMERS_PATH + LEDGER, {
            fromDate: toIsoDate(filter.start),
            toDate: toIsoDate(filter.end),
            isOpen: filter.isOpen,
            name: filter.textQuery
        })
    } catch (e: any) {
        console.error(e)
    }
}

const fetchSubAccountLedger = async (subAccountRelationType: SubAccountRelationType, subAccountUuid: string, filter: SubAccountLedgerFilter, tenantId?: string): Promise<LedgerSubAccount | undefined> => {
    if (filter.isOpen || !filter.start) {
        filter.start = SYS_MIN_DATE
    }
    const relationPath: string = subAccountRelationType === CUSTOMER ? 'customer/' : 'supplier/'

    try {
        return await get(SUB_ACCOUNT_PATH + '/' + relationPath + subAccountUuid + LEDGER, {
            fromDate: toIsoDate(filter.start),
            toDate: toIsoDate(filter.end),
            isOpen: filter.isOpen
        }, tenantId)
    } catch (e: any) {
        console.error(e)
    }
}

const fetchSubAccountLines = async (subAccountUuid: string, filter: SubAccountLedgerFilter): Promise<Page<SubAccountLine>> => {
    if (filter.isOpen || !filter.start) {
        filter.start = SYS_MIN_DATE
    }
    return await get<Page<SubAccountLine>>(SUB_ACCOUNT_PATH + '/' + subAccountUuid + '/lines', {
        fromDate: toIsoDate(filter.start),
        toDate: toIsoDate(filter.end),
        isOpen: filter.isOpen
    }) ?? emptyPage
}

const fetchAgeDividedReport = async (relationType: SubAccountRelationType, page: number = 0, size: number = 10): Promise<Page<AgeDividedReportEntry>> => {
    return await get(`${SUB_ACCOUNT_PATH}/${relationType}/lines/report`,
        { page, size }
    ) ?? emptyPage
}

// TODO: modify backend to take a lines list instead
const connectLines = async (connections: SaccLineConnUuid[]) => {
    try {
        return await post(SUB_ACCOUNT_PATH + '/connect-lines', connections)
    } catch (e: any) {
        notifyError({ title: 'Feilet', description: { descriptionMessage: 'Klarte ikke å koble linjer.', error: e } })
        throw e
    }
}

const disconnectLine = async (lineUuid: string) => {
    try {
        return await post(SUB_ACCOUNT_PATH + '/disconnect-line', { uuid: lineUuid })
    } catch (e: any) {
        notifyError({ title: 'Feilet', description: { descriptionMessage: `Klarte ikke å koble fra linje med id: ${lineUuid}`, error: e } })
        throw e
    }
}
let initialized = false
const init = async () => {
    if (initialized) return
    initialized = true
    await fetchSubAccounts()
}
const clear = () => {
    customers.value = []
    suppliers.value = []
    clearContaCommands()
}

type NewSubAccountProps = {
    relationType: SubAccountRelationType,
    isBusiness?: Boolean,
    name?: string,
    orgNo?: string,
}

//TODO: Where should we emit event that subaccounts have been updated? API?
const showNewSubAccountPopup = (props: NewSubAccountProps) => {
    loadComponent(NewSubAccountPopup, props)
}

const clearContaCommands = () => {
    unregisterCommandsOfType(CUSTOMER_CONTA_COMMAND_TYPE)
    unregisterCommandsOfType(SUPPLIER_CONTA_COMMAND_TYPE)
}

const SUB_ACCOUNT_CREATED = 'subAccountCreated'
const SUB_ACCOUNT_UPDATED = 'subAccountUpdated'
const SUB_ACCOUNT_DELETED = 'subAccountDeleted'

const useSubAccounts = () => {

    const { onEvents, cleanUp } = useSingletonEmitter('subAccounts')
    onMounted(async () => {
        await orgSelectionComplete
        await init()
        onEvents([ORG_SELECTED, ORG_SWITCHED, SUB_ACCOUNT_CREATED, SUB_ACCOUNT_UPDATED, SUB_ACCOUNT_DELETED], () => {
            clearContaCommands()
            fetchSubAccounts()
        })
        onEvents([NO_ORG_SELECTED, LOGOUT], clear)
    })

    onUnmounted(() => {
        cleanUp()
    })
    return {
        customers,
        suppliers,
        getAccountsReceivable,
        getAccountsPayable,
        fetchSubAccountLedger,
        createSubAccount,
        updateSubAccount,
        findSubAccountByUuid,
        findTransactionLinesBySubAccountUuid,
        fetchSubAccountLines,
        closePosts: connectLines,
        disconnectLine
    }
}

const useSubAccountForm = (initialSubAccount: SubAccount | NewSubAccount) => {
    const data = ref(cloneDeep(initialSubAccount))
    const original = ref(initialSubAccount)
    const subAccountValidator = useSubAccountValidation()

    const isNew = computed(() => !(data.value as SubAccount).uuid)
    const isDirty = computed(() => !isEqual(original.value, data.value))
    const isValid = computed(() => subAccountValidator.isValid(data.value))
    const isCustomer = computed(() => data.value?.relationType === CUSTOMER)
    const isBusiness = computed(() => data.value?.isBusiness)

    const reset = () => {
        data.value = cloneDeep(original.value)
    }

    const fetchTransactionCountForSubAccount = async () => {
        const searchParams = ref<TransactionSearchParams>({
            subAccountUuid: (data.value as SubAccount).uuid,
            page: 0
        })

        const transactionLines = await findTransactionLinesBySubAccountUuid(searchParams.value)
        return transactionLines.length
    }

    return { data, isNew, isDirty, isValid, isCustomer, isBusiness, reset, fetchTransactionCountForSubAccount }
}

export {
    getAccountsReceivable,
    getAccountsPayable,
    updateSubAccount,
    useSubAccounts,
    findSubAccountByUuid,
    findSubAccountByOrgNo,
    findSubAccountByName,
    findSupplierByUuid,
    findSupplierByName,
    findSupplierByOrgNo,
    findCustomerByName,
    findCustomerByOrgNo,
    findTransactionLinesBySubAccountUuid,
    validateAddressAndContactInfo,
    validateDebtCollectionInfo,
    createSubAccount,
    getCustomers,
    fetchCustomers,
    getSuppliers,
    fetchSuppliers,
    fetchAgeDividedReport,
    deleteSubAccount,
    showNewSubAccountPopup,
    useSubAccountForm,
    SUB_ACCOUNT_CREATED
}